import { ChevronRight } from 'lucide-react';
import { createContext, useContext, useLayoutEffect, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { DropdownMenu as RMenu, Popover as RPopover, Tooltip as RTooltip } from 'radix-ui';
import { cx } from '../lib/cx';
import { CheckGlyph } from './icons';
import s from './Overlay.module.css';

/* ---------- Menu (dropdown), on Radix DropdownMenu: roving focus, typeahead, Esc ---------- */

export const Menu = RMenu.Root;
export const MenuTrigger = RMenu.Trigger;
export const MenuGroup = RMenu.Group;

/**
 * A menu item that opens a dialog closes the menu, and Radix then gives focus back to the menu's button, taking it out
 * of the dialog that just opened (ADR-638). With a dialog open the menu leaves focus alone; the dialog gives it back to
 * the menu's button when it closes (lib/focusMemory).
 */
export const keepFocusInDialog = (event: Event) => {
  if (typeof document !== 'undefined' && document.querySelector('[role=dialog],[role=alertdialog]'))
    event.preventDefault();
};

export function MenuContent({
  className,
  sideOffset = 4,
  align = 'start',
  collisionPadding = 8,
  onCloseAutoFocus,
  ...rest
}: ComponentPropsWithoutRef<typeof RMenu.Content>) {
  return (
    <RMenu.Portal>
      <RMenu.Content
        className={cx(s.popover, s.menu, className)}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        onCloseAutoFocus={(e) => {
          onCloseAutoFocus?.(e);
          if (!e.defaultPrevented) keepFocusInDialog(e);
        }}
        {...rest}
      />
    </RMenu.Portal>
  );
}

/**
 * A menu closes at once when an item is chosen (ADR-1031): the item usually does something at once (opens a dialog or a
 * popover that takes focus), and a menu still fading out beside it would take the focus back. Choosing marks every open
 * menu `data-picked`, which base.css gives no exit animation. An item that keeps the menu open (`preventDefault`) doesn't.
 */
const closingAtOnce = (onSelect?: (event: Event) => void) => (event: Event) => {
  onSelect?.(event);
  if (!event.defaultPrevented) document.querySelectorAll('[role=menu]').forEach((m) => m.setAttribute('data-picked', ''));
};

/** `shortcutChip` draws the key as a grey rounded chip (Quick add's letters) instead of plain text. */
export function MenuItem({
  className,
  icon,
  shortcut,
  shortcutChip,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof RMenu.Item> & {
  icon?: ReactNode;
  shortcut?: string;
  shortcutChip?: boolean;
}) {
  return (
    <RMenu.Item className={cx(s.item, className)} {...rest} onSelect={closingAtOnce(rest.onSelect)}>
      {rest.asChild ? (
        children
      ) : (
        <>
          {icon}
          <span>{children}</span>
          {shortcut && <kbd className={cx(s.shortcut, shortcutChip && s.shortcutChip)}>{shortcut}</kbd>}
        </>
      )}
    </RMenu.Item>
  );
}

export function MenuGroupLabel({ className, ...rest }: ComponentPropsWithoutRef<typeof RMenu.Label>) {
  return <RMenu.Label className={cx(s.groupLabel, className)} {...rest} />;
}

export function MenuSeparator({ className, ...rest }: ComponentPropsWithoutRef<typeof RMenu.Separator>) {
  return <RMenu.Separator className={cx(s.separator, className)} {...rest} />;
}

export const MenuRadioGroup = RMenu.RadioGroup;

export function MenuRadioItem({
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof RMenu.RadioItem>) {
  return (
    <RMenu.RadioItem className={cx(s.item, className)} {...rest} onSelect={closingAtOnce(rest.onSelect)}>
      <span>{children}</span>
      <RMenu.ItemIndicator className={s.itemCheck}>
        <CheckGlyph />
      </RMenu.ItemIndicator>
    </RMenu.RadioItem>
  );
}

/* ---------- Submenu: an item that opens a second menu beside it (→ or Enter opens it, ← or Esc goes back) ---------- */

export const MenuSub = RMenu.Sub;

export function MenuSubTrigger({
  className,
  icon,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof RMenu.SubTrigger> & { icon?: ReactNode }) {
  return (
    <RMenu.SubTrigger className={cx(s.item, s.subTrigger, className)} {...rest}>
      {icon}
      <span>{children}</span>
      <ChevronRight aria-hidden className={s.subChevron} />
    </RMenu.SubTrigger>
  );
}

export function MenuSubContent({
  className,
  sideOffset = 4,
  ...rest
}: ComponentPropsWithoutRef<typeof RMenu.SubContent>) {
  return (
    <RMenu.Portal>
      <RMenu.SubContent className={cx(s.popover, s.menu, className)} sideOffset={sideOffset} {...rest} />
    </RMenu.Portal>
  );
}

/* ---------- Popover ---------- */

export const Popover = RPopover.Root;
export const PopoverTrigger = RPopover.Trigger;
export const PopoverClose = RPopover.Close;
export const PopoverAnchor = RPopover.Anchor;

export function PopoverContent({
  className,
  sideOffset = 4,
  collisionPadding = 8,
  ...rest
}: ComponentPropsWithoutRef<typeof RPopover.Content>) {
  return (
    <RPopover.Portal>
      <RPopover.Content
        className={cx(s.popover, className)}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...rest}
      />
    </RPopover.Portal>
  );
}

/* ---------- Tooltip ---------- */

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Asked each time the tooltip would open (hover or keyboard focus), and again after each render while it's open;
   * `false` keeps or closes it. For a full-text tooltip that shows only while the visible text is cut off with an
   * ellipsis (the text can change while it's open, e.g. after switching business).
   */
  shouldOpen?: () => boolean;
}

/** Set by `TooltipProvider`: tooltips below it share one delay timer, so a second tooltip appears at once (ADR-1034). */
const SharedTooltips = createContext(false);

/** The pause before the first tooltip, and how soon after one closes the next still opens at once. */
const TOOLTIP_DELAY_MS = 300;
const TOOLTIP_SKIP_MS = 300;

/**
 * Mount once near the root. Radix keeps the "a tooltip was just open" memory in its Provider, so tooltips must share
 * one for the rule to work: the first waits `TOOLTIP_DELAY_MS`, the ones that follow while it is open (or within
 * `TOOLTIP_SKIP_MS` of it closing) open with no delay and no animation (the `instant-open` state, base.css).
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <SharedTooltips.Provider value>
      <RTooltip.Provider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={TOOLTIP_SKIP_MS}>
        {children}
      </RTooltip.Provider>
    </SharedTooltips.Provider>
  );
}

/**
 * Dark tooltip (cui5-tooltip). Wraps one focusable child. Icon-only buttons still need their own
 * `aria-label`; the tooltip is a visual aid. A disabled control gets one too: it states the reason.
 */
export function Tooltip({ content, children, side = 'bottom', shouldOpen }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const shared = useContext(SharedTooltips);
  // Controlled only when the caller decides; otherwise Radix keeps its own state, as before.
  const control = shouldOpen ? { open, onOpenChange: (next: boolean) => setOpen(next && shouldOpen()) } : {};
  useLayoutEffect(() => {
    if (open && shouldOpen && !shouldOpen()) setOpen(false);
  });
  const tip = (
    <RTooltip.Root {...control}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content className={s.tooltip} data-tip="" side={side} sideOffset={6}>
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
  // Without a shared provider (a test, a page outside the shell) the tooltip keeps its own, as before.
  return shared ? tip : <RTooltip.Provider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={TOOLTIP_SKIP_MS}>{tip}</RTooltip.Provider>;
}
